import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";

const WA_NUMBER_DISPLAY = "+91 91372 87747";
const WA_LINK = "https://wa.me/919137287747?text=" + encodeURIComponent("Hi! I'd like to know more about MeraStartup.");
const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const SID_KEY = "ms_chat_sid";

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
      <path d="M16.04 3C9.4 3 4 8.4 4 15.04c0 2.12.55 4.19 1.6 6.02L4 29l8.13-1.55a12 12 0 0 0 3.9.65h.01C22.68 28.1 28 22.7 28 16.06 28 9.42 22.68 3 16.04 3Zm0 22.13h-.01a9.9 9.9 0 0 1-3.87-.77l-.28-.11-4.82.92.99-4.7-.18-.29a9.86 9.86 0 0 1-1.5-5.27c0-5.46 4.45-9.9 9.93-9.9 2.65 0 5.14 1.04 7.01 2.91a9.83 9.83 0 0 1 2.9 7C25.95 20.7 21.5 25.13 16.04 25.13Zm5.45-7.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.62.71.23 1.36.2 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
    </svg>
  );
}

export default function ChatWidget() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm Mira 👋 Ask me anything about MeraStartup — features, pricing, or how to launch your store.", showPricing: false },
  ]);
  const scrollRef = useRef(null);
  const sid = useRef(localStorage.getItem(SID_KEY) || null);

  useEffect(() => {
    api.get("/chatbot/config").then(({ data }) => setEnabled(!!data.enabled)).catch(() => setEnabled(false));
    api.get("/landing").then(({ data }) => setPlans(data?.pricing?.plans || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chatbot/message", { message: text, session_id: sid.current });
      if (data.session_id) { sid.current = data.session_id; localStorage.setItem(SID_KEY, data.session_id); }
      setMessages((m) => [...m, { role: "assistant", text: data.reply, showPricing: !!data.show_pricing }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "The assistant is busy. Please try again, or chat on WhatsApp.";
      setMessages((m) => [...m, { role: "assistant", text: msg }]);
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) {
    // AI chat unavailable (no key configured) — render nothing.
    return null;
  }

  return (
    <>
      {/* Floating chat button (no auto-popup, no notification badge) */}
      {!open && (
        <button type="button" onClick={() => setOpen(true)} data-testid="chat-fab"
                className="fixed bottom-20 right-4 sm:bottom-5 sm:right-5 z-40 w-14 h-14 rounded-full bg-orange-500 text-white shadow-lg flex items-center justify-center hover:bg-orange-600 hover:scale-105 transition-all border-2 border-stone-900">
          <MessageCircle className="w-7 h-7" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div data-testid="chat-panel" className="fixed z-50 bg-white border-2 border-stone-900 shadow-2xl flex flex-col
             inset-x-3 bottom-3 top-16 rounded-2xl
             sm:inset-auto sm:bottom-5 sm:right-5 sm:top-auto sm:w-[380px] sm:h-[560px] sm:max-h-[80vh]">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-stone-900 text-white rounded-t-2xl">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold leading-tight">Mira · MeraStartup</div>
              <div className="text-xs text-emerald-300 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online now</div>
            </div>
            <a href={WA_LINK} target="_blank" rel="noreferrer" data-testid="chat-whatsapp"
               className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center hover:scale-105 transition-transform" title="Chat on WhatsApp">
              <WhatsAppIcon className="w-5 h-5" />
            </a>
            <button type="button" onClick={() => setOpen(false)} data-testid="chat-close" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50">
            {messages.map((m, i) => (
              <div key={i}>
                <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-orange-500 text-white rounded-br-sm" : "bg-white border border-stone-200 text-stone-800 rounded-bl-sm"}`}>
                    {m.text}
                  </div>
                </div>
                {m.showPricing && plans.length > 0 && (
                  <div className="mt-2 rounded-xl border-2 border-stone-900 overflow-hidden bg-white" data-testid="chat-pricing-table">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-stone-900 text-white">
                          <th className="text-left p-2 font-semibold">Plan</th>
                          <th className="text-right p-2 font-semibold">Price</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {plans.map((p) => (
                          <tr key={p.id} className="border-t border-stone-200">
                            <td className="p-2 align-top">
                              <div className="font-bold text-stone-900">{p.name}</div>
                              <div className="text-stone-500 leading-tight">{p.tagline}</div>
                            </td>
                            <td className="p-2 text-right align-top font-bold font-mono-nums whitespace-nowrap">{rupee(p.price)}<span className="text-stone-400 font-normal">/{p.period || "yr"}</span></td>
                            <td className="p-2 align-top text-right">
                              <Link to={`/signup?plan=${p.id}`} onClick={() => setOpen(false)} className="inline-block rounded-full bg-orange-500 text-white px-2.5 py-1 font-bold hover:bg-orange-600">Choose</Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Mira is typing…</div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="p-3 border-t border-stone-200 flex items-center gap-2 bg-white rounded-b-2xl">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="chat-input"
              placeholder="Ask about features, pricing…"
              className="flex-1 h-10 px-3 rounded-full border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send" className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 disabled:opacity-50 flex-shrink-0">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
